import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export const Route = createFileRoute("/cuenta/login")({
  head: () => ({ meta: [{ title: "Iniciar sesión — Truck Company Center" }, { name: "robots", content: "noindex" }] }),
  component: Login,
});

// Internal-only redirects: must start with "/" but not "//" (avoid open-redirect to external sites).
function safeRedirect(): string | null {
  if (typeof window === "undefined") return null;
  const r = new URL(window.location.href).searchParams.get("redirect");
  if (!r) return null;
  if (!r.startsWith("/") || r.startsWith("//")) return null;
  return r;
}

async function resolveDestinationAfterLogin(userId: string): Promise<string> {
  const redirect = safeRedirect();
  if (redirect) return redirect;
  const { data } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  return data ? "/admin" : "/cuenta";
}

function Login() {
  const nav = useNavigate();
  const [mode, setMode] = useState<"password" | "magic">("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  async function loginPassword(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setLoading(false);
      if (error.message.toLowerCase().includes("invalid")) {
        toast.error("Email o contraseña incorrectos. ¿Olvidaste tu contraseña?");
      } else {
        toast.error(error.message);
      }
      return;
    }
    const dest = await resolveDestinationAfterLogin(data.user!.id);
    setLoading(false);
    toast.success("Sesión iniciada");
    // Use full reload for /admin so the gate re-runs with fresh session
    if (dest.startsWith("/admin")) window.location.href = dest;
    else nav({ to: dest as any });
  }

  async function loginMagic(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    const redirect = safeRedirect();
    // Forward redirect through the magic-link callback so user lands where intended after clicking the email.
    const callback = `${window.location.origin}/cuenta/login${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""}`;
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: callback },
    });
    setLoading(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Te enviamos un enlace mágico al email");
  }

  return (
    <div className="mx-auto max-w-md px-4 py-16 md:px-6">
      <h1 className="font-display text-3xl font-semibold text-foreground">Iniciar sesión</h1>
      <p className="mt-2 text-sm text-muted-foreground">Accede a tu cuenta para ver tus pedidos y formularios.</p>

      <div className="mt-6 flex gap-2 rounded-md border border-hairline bg-surface p-1">
        <button
          type="button"
          onClick={() => setMode("password")}
          className={`flex-1 rounded px-3 py-1.5 text-xs ${mode === "password" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
        >
          Email + contraseña
        </button>
        <button
          type="button"
          onClick={() => setMode("magic")}
          className={`flex-1 rounded px-3 py-1.5 text-xs ${mode === "magic" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
        >
          Enlace mágico
        </button>
      </div>

      <form onSubmit={mode === "password" ? loginPassword : loginMagic} className="mt-4 space-y-3 rounded-xl border border-hairline bg-surface p-6">
        <div>
          <Label htmlFor="login-email" className="text-xs text-muted-foreground">Email</Label>
          <Input id="login-email" className="bg-surface-2 border-hairline" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        {mode === "password" && (
          <div>
            <Label htmlFor="login-password" className="text-xs text-muted-foreground">Contraseña</Label>
            <Input id="login-password" className="bg-surface-2 border-hairline" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
        )}
        <Button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
          {loading ? "..." : mode === "password" ? "Entrar" : "Enviar enlace"}
        </Button>
      </form>

      <div className="mt-4 flex flex-col items-center gap-2 text-xs">
        <Link to="/cuenta/recuperar" className="text-muted-foreground hover:text-foreground">¿Olvidaste tu contraseña?</Link>
        <Link to="/cuenta/registro" className="text-muted-foreground hover:text-foreground">¿No tienes cuenta? Crear una</Link>
      </div>
    </div>
  );
}
